import { IBarrio, IComuna, IRegion, UrlSlug } from "@toctoc/lista-seo-common";
import { GetStaticPropsParams, ListaSEOType, ParsedUrlSegments, RegionLink } from "./types";
import { IEstructuraPrincipal, IForcedOrderProperties, IValues } from "@/components/templates/search/interfaces/IEstructuraPrincipal";
import { IFiltrosBase } from "@/components/templates/search/interfaces/IFiltrosBase";
import { separadorDeMiles } from "./separadorDeMiles";
import { STATUS } from '@/app/globalContext/globalContext.interfaces'
import { GetStaticPropsContext } from "next";
import { determineRegionSlugWithUrl } from "@/components/particles/prefooter/service";
import { IPropertyCard } from "@/components/templates/search/interfaces";

export const parseUrlSegments = (path: string[], urlSlug?: Array<{ idType: UrlSlug }>): ParsedUrlSegments => {
	if (urlSlug && urlSlug.length > 0) {
		// Flexible structure - use urlSlug to map segments
		const segments: ParsedUrlSegments = {};

		urlSlug.forEach((slugInfo, index) => {
			if (index < path.length) {
				const segment = path[index];

				switch (slugInfo.idType) {
					case UrlSlug.CONCEPT:
						segments.concept = segment;
						break;
					case UrlSlug.OPERATION_TYPE:
						segments.operationType = segment;
						break;
					case UrlSlug.PROPERTY_TYPE:
						segments.propertyType = segment;
						break;
					case UrlSlug.REGION:
						segments.region = segment;
						break;
					case UrlSlug.COMMUNE:
						segments.commune = segment;
						break;
					case UrlSlug.NEIGHBORHOOD:
						segments.neighborhood = segment;
						break;
					case UrlSlug.AVENUE:
						segments.avenue = segment;
						break;
					case UrlSlug.STREET:
						segments.street = segment;
						break;
					case UrlSlug.LANE:
						segments.lane = segment;
						break;
					case UrlSlug.CHAIN:
						segments.chain = segment;
						break;
					case UrlSlug.CLIENT_TYPE:
						segments.clientType = segment;
						break;
					case UrlSlug.PROJECT_ORDER:
						segments.projectOrder = segment;
						break;
				}
			}
		});

		return segments;
	} else {
		const segments: ParsedUrlSegments = {};

		if (path.length >= 1) {
			segments.operationType = path[0]?.toLowerCase();
		}
		if (path.length >= 2) {
			segments.propertyType = path[1]?.toLowerCase();
		}
		if (path.length >= 3) {
			segments.region = path[2]?.toLowerCase();
		}
		if (path.length >= 4) {
			segments.commune = path[3]?.toLowerCase();
		}

		return segments;
	}
};

export const getBreadcrumbsSegments = (urlSegments: ParsedUrlSegments, pathListaSeo: string[]) => {
	let chainShift = 0;

	const chainSegment = urlSegments?.chain || '';

	if (chainSegment) {
		chainShift = 1;
	}

	//Se quito urlSegments por que afecta el funcionamiento para las listas seo personalizadas y se logra lo mismo usando pathListaSeo solamente
	/*const tof = pathListaSeo.length > 0 ? (urlSegments?.operationType || pathListaSeo[0]) : '';
	const tpf = pathListaSeo.length > (1 + chainShift) ? (urlSegments?.propertyType || pathListaSeo [1 + chainShift]) : '';
	const region = pathListaSeo.length > (2 + chainShift) ? (urlSegments?.region || pathListaSeo[2 + chainShift]) : '';
	const comuna = pathListaSeo.length > (3 + chainShift) ? (urlSegments?.commune || pathListaSeo[3 + chainShift]) : '';*/

	const tof = pathListaSeo?.[0] ?? '';
	const tpf = pathListaSeo?.[1 + chainShift] ?? '';
	const region = pathListaSeo?.[2 + chainShift] ?? '';
	const comuna = pathListaSeo?.[3 + chainShift] ?? '';

	return {
		tof,
		tpf,
		region,
		comuna,
		chainSegment
	}
}

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint, no-unused-vars
export const setEstructuraValues = <T extends unknown>(estructuraPrincipal: IEstructuraPrincipal, idString: string, formatFn: (input: T[]) => IValues[], collection: T[]) => {
	if (collection === undefined || collection === null || collection.length === 0)
		return estructuraPrincipal;
	const estructuraPrincipalRegion = estructuraPrincipal.filtros.find(f => f.id === idString);
	if (estructuraPrincipalRegion !== undefined) {
		const estructuraPrincipalRegionIndex = estructuraPrincipal.filtros.indexOf(estructuraPrincipalRegion);

		estructuraPrincipal.filtros[estructuraPrincipalRegionIndex] = {
			...estructuraPrincipalRegion,
			values: formatFn(collection)
		}
	}

	return estructuraPrincipal;
}

export const comunaRegion = (filtrosBase: IFiltrosBase[]) => {
	const comuna = filtrosBase.find(x => x.id === 'comuna');
	const region = filtrosBase.find(x => x.id === 'region');
	return comuna ? comuna.values[0].label : (region ? region.values[0].label : "");
};

// Generar bajada de título: usar descripción del SEO dinámico si está disponible, sino la lógica original
export const generateBajadaTitulo = (propertiesCountState: number, filtrosBase: IFiltrosBase[]) => {
	// Siempre generar la información del conteo de propiedades
	let baseMessage = "";
	if (propertiesCountState === 0) {
		baseMessage = "No se encontraron propiedades.";
	} else if (propertiesCountState === 1) {
		baseMessage = `${propertiesCountState} propiedad encontrada ${comunaRegion(filtrosBase) !== "" ? ` en ${comunaRegion(filtrosBase)}` : comunaRegion(filtrosBase)}`;
	} else {
		baseMessage = `${separadorDeMiles(propertiesCountState)} propiedades encontradas${comunaRegion(filtrosBase) !== "" ? ` en ${comunaRegion(filtrosBase)}` : comunaRegion(filtrosBase)}.`;
	}

	// Si hay SEO dinámico y es diferente al original, podríamos usar su información adicional
	// Por ahora, mantener solo el conteo de propiedades que es la información más importante
	return baseMessage;
};

// Funciones de formato para poblamiento de filtros — usadas tanto en SSR como en CSR (shallow nav)
export const formatRegionValues = (regiones: IRegion[]): IValues[] =>
	regiones
		.filter(r => !r.descripcion.includes("Seleccione"))
		.map(region => ({ label: region.descripcion, value: [+region.idRegion], id: +region.idRegion }))

export const formatComunaValues = (comunas: IComuna[]): IValues[] =>
	comunas.map(comuna => ({ label: comuna.descripcion, value: [+comuna.idComuna], id: +comuna.idComuna }))

export const formatBarrioValues = (barrios: IBarrio[]): IValues[] =>
	barrios.map(barrio => ({ label: barrio.label, value: barrio.value, id: barrio.id }))

export const getCommunesAndNeighborhoodsList = async (filtrosBase: IFiltrosBase[], regiones: IRegion[], AllowedRemoteHeaders: Record<string, string>, getComunas: (input?: number | undefined, headers?: Record<string, string> | undefined, pathList?: string) => Promise<IComuna[]>, getBarrios: (input?: number | undefined, headers?: Record<string, string> | undefined) => Promise<IBarrio[]>, pathList?: string) => {
	const listaComunas: IComuna[] = [];
	const listaBarrios: IBarrio[] = [];
	const currentRegionId = filtrosBase.find(r => r.id === "region");
	const matchedRegion = regiones.find(r => currentRegionId?.values?.find(cr => cr.id === +r.idRegion));

	if (matchedRegion !== undefined) {
		const comuna = await getComunas(+matchedRegion?.idRegion, undefined, pathList);

		if (comuna !== undefined) {
			listaComunas.push(...comuna.map(c => ({ descripcion: c.descripcion, idComuna: c.idComuna, idRegion: matchedRegion?.idRegion })))

			// Load barrios if there's a specific comuna selected
			const currentComunaId = filtrosBase.find(c => c.id === "comuna")
			if (currentComunaId?.values?.[0]?.id) {
				const comunaNumericId = +currentComunaId.values[0].id;
				const barrio = await getBarrios(comunaNumericId, AllowedRemoteHeaders);
				if (barrio !== undefined) {
					listaBarrios.push(...barrio.map(b => ({ id: b.id.toString(), label: b.label, value: b.value })))
				}
			}
		}
	}

	return {
		listaComunas,
		listaBarrios
	}
}

export const sortPropertiesByForcedOrder = (
	properties: IPropertyCard[],
	forcedOrder: IForcedOrderProperties
) => {
	const { field, order } = forcedOrder;

	// Creamos un mapa de posiciones según el orden forzado
	const orderMap = new Map(order.map((value, index) => [value, index]));

	return [...properties].sort((a, b) => {
		const aValue = a[field as keyof IPropertyCard];
		const bValue = b[field as keyof IPropertyCard];

		const aIndex = orderMap.has(aValue) ? orderMap.get(aValue)! : Infinity;
		const bIndex = orderMap.has(bValue) ? orderMap.get(bValue)! : Infinity;

		// Ordenamos primero según el índice definido en order
		if (aIndex !== bIndex) return aIndex - bIndex;

		return 0;
	});
}

export const getListaSeoStaticProps = async (
	context: GetStaticPropsContext,
	pathPrefix: string[]
) => {
	const { params } = context;
	// TODO: Replace with current API DATA
	const AllowedRemoteHeaders = { "originkey": "somoslosmejores", cache: 'force-cache' }
	const { pathListaSeo = [] } = params as GetStaticPropsParams;

	const fullPath: Array<string> = [...pathPrefix, ...pathListaSeo];

	const { setupServices } = (await import('../../services.setup.client'));
	const {
		IS_PROD,
		NEXT_PUBLIC_ID_VWO,
		urlBase,
		//urlTocTocImg,
		idGTM,
		getRegiones,
		getComunas,
		getBarrios,
		getEstructuraPrincipalWithoutSlug,
		determineRegionSlug,
		getPropiedades,
		cloudfrontImgUrl,
		quoteAndContactEnabled
	} = setupServices();

	const [
		responseGetEstructuraPrincipal,
		regiones,
		layoutStaticPropsImport,
		filtersUtilImport
	] = await Promise.all([
		getEstructuraPrincipalWithoutSlug(fullPath, AllowedRemoteHeaders),
		getRegiones(AllowedRemoteHeaders, fullPath.join('/')),
		import("@/components/templates/menu/view/menuNuevo"),
		import("@/components/templates/filters/util")
	]);

	const { loadLayoutStaticProps } = layoutStaticPropsImport;
	const { convertFiltrosBaseToFiltros } = filtersUtilImport;
	let estructuraPrincipal: IEstructuraPrincipal = responseGetEstructuraPrincipal;
	const urlSegments = parseUrlSegments(fullPath, responseGetEstructuraPrincipal.urlSlug);

	// Extract region slug and compute region links
	let regionSlug = urlSegments.region || null;
	let computedRegionSlug: RegionLink[] | null = null;

	if (regionSlug) {
		computedRegionSlug = determineRegionSlugWithUrl(regionSlug);
	} else if (!responseGetEstructuraPrincipal.urlSlug && fullPath.length >= 3) {
		// Fallback to old logic for rigid structure (only if we have region segment)
		const _regionSlug = fullPath[2]?.toLowerCase();
		if (_regionSlug) {
			regionSlug = _regionSlug;
			// Create array with available segments, filtering out empty ones
			const segments = [
				fullPath[0]?.toLowerCase(),
				fullPath[1]?.toLowerCase(),
				_regionSlug,
				fullPath[3]?.toLowerCase()
			].filter(Boolean);
			computedRegionSlug = determineRegionSlug(segments);
		}
	}

	// Check if the requested page exists
	if (!responseGetEstructuraPrincipal ||
		!responseGetEstructuraPrincipal.filtros ||
		responseGetEstructuraPrincipal.filtros.length === 0 ||
		!responseGetEstructuraPrincipal.filtroBase) {
		console.warn("[getStaticProps] The requested path does not exists ", fullPath.join("/"));
		return undefined;
	}
	const filtrosBase = responseGetEstructuraPrincipal.filtroBase;

	const layoutStaticProps = await loadLayoutStaticProps();
	let listaComunas: IComuna[] = [];
	let listaBarrios: IBarrio[] = [];

	// Convert filtroBase to proper format before using
	const convertedFiltrosBase = convertFiltrosBaseToFiltros(filtrosBase);

	// Use all filtrosBase for properties fetching (they are all pre-applied)
	const propiedades = await getPropiedades({ filtros: convertedFiltrosBase, forcedOrderProperties: estructuraPrincipal?.forcedOrderProperties }) || { results: [], total: 0, page: 1 };

	if (regiones !== null && regiones.length > 0 && regionSlug !== undefined && filtrosBase !== undefined) {
		const lists = await getCommunesAndNeighborhoodsList(filtrosBase, regiones, AllowedRemoteHeaders, getComunas, getBarrios, estructuraPrincipal?.url);
		listaComunas = lists.listaComunas;
		listaBarrios = lists.listaBarrios;
	}
	const comunas: IComuna[] = regiones !== null && regiones.length > 0 ? listaComunas : [];
	const barrios: IBarrio[] = comunas !== null && comunas.length > 0 ? listaBarrios : [];

	// How to add data to filters
	estructuraPrincipal = setEstructuraValues<IRegion>(estructuraPrincipal,
		"region",
		formatRegionValues,
		regiones
	)

	estructuraPrincipal = setEstructuraValues<IComuna>(estructuraPrincipal,
		"comuna",
		formatComunaValues,
		comunas
	)

	estructuraPrincipal = setEstructuraValues<IBarrio>(estructuraPrincipal,
		"barrio",
		formatBarrioValues,
		barrios
	)

	const { results, total, page } = propiedades;

	return {
		initialReduxState: {
			PropertyState: {
				results: results || [],
				total: total || 0,
				page: page || 1,
				allResults: [],
				batchStartPage: 1,
				// Always start with init status to allow client to decide whether to show loading
				status: STATUS.init
			},
			FiltersAvailableState: {
				filtros: estructuraPrincipal.filtros || [],
				status: STATUS.idle
			},
			FilterState: {
				// Initialize with ALL filtroBase - they are all pre-applied filters (converted to proper format)
				filtros: convertedFiltrosBase || [],
				order: 1,
				page: 1,
				status: 0,
				forcedOrderProperties: estructuraPrincipal?.forcedOrderProperties ?? null
			}
		},
		propiedades: propiedades || { results: [], total: 0, page: 1 },
		totalPropiedades: 0,
		urlBase: urlBase || '',
		listaSeoUrl: responseGetEstructuraPrincipal.url ?? '',
		urlTocTocImg: cloudfrontImgUrl || '',
		idGTM: idGTM || '',
		estructuraPrincipal,
		layoutVersion: responseGetEstructuraPrincipal.layoutVersion ?? 'v1',
		// Pass the complete filtrosBase for backward compatibility
		filtrosBase: filtrosBase || [],
		computedRegionSlug: computedRegionSlug || null,
		urlSegments: urlSegments || {},
		NEXT_PUBLIC_ID_VWO: NEXT_PUBLIC_ID_VWO || '',
		IS_PROD: IS_PROD || false,
		pathPrefix,
		quoteAndContactEnabled: quoteAndContactEnabled || false,
		...layoutStaticProps
	} as ListaSEOType
}